import React, { useState, useEffect, useCallback } from 'react';
import SearchBar from './SearchBar';
import RoomCard from './RoomCard';
import BookingSidebar from './BookingSidebar';
import ImageModal from './ImageModal';
import AuthModal from './AuthModal';
import { useBooking } from '../context/BookingContext';
import { apiService } from '../services/apiService';

const RoomSelection = ({ navigateTo }) => {
  const { state, dispatch, formatCurrency } = useBooking();
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [hotels, setHotels] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false); // Add this for auto-search
  const [initialLoad, setInitialLoad] = useState(true); // Add this

  const [user, setUser] = useState(null);
  /* VANSH START - Mobile sidebar state */
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  /* VANSH END */

   const fetchHotels = useCallback(async (searchTerm = '') => {
    try {
      const hotelData = await apiService.getHotelList(searchTerm);
      setHotels(hotelData);
      
      // Only set default hotel if we don't already have one selected
    if (hotelData.length > 0 && initialLoad && searchTerm === '') {
      // Check if we have a saved hotel in state or session storage
      const savedHotelId = state.searchState.hotelId;
      
      console.log('📋 Debug - Initial Load:', {
        initialLoad,
        searchTerm,
        hotelDataCount: hotelData.length,
        savedHotelId,
        currentHotel: state.searchState.hotel,
        currentHotelId: state.searchState.hotelId,
        hotelsList: hotelData.map(h => ({ id: h.id, name: h.name }))
      });
      
      // IMPORTANT: If we already have a hotel selected, don't change it
      if (savedHotelId && state.searchState.hotel && state.searchState.hotel !== "ABC Hotel") {
        console.log('✅ Already have a selected hotel:', state.searchState.hotel, 'keeping it');
        setInitialLoad(false);
        return; // Exit early - don't change the selection
      }
      
      if (savedHotelId) {
        // Try to find the saved hotel in the fetched list
        const savedHotel = hotelData.find(h => h.id === savedHotelId);
        console.log('🔍 Looking for saved hotel:', savedHotelId, 'Found:', savedHotel);
        
        if (savedHotel) {
          // Update state with the saved hotel
          console.log('✅ Found saved hotel, selecting:', savedHotel.name);
          dispatch({
            type: 'UPDATE_SEARCH_STATE',
            payload: { 
              hotel: savedHotel.name, 
              hotelId: savedHotel.id 
            }
          });
        } else {
          // Saved hotel not found in list, use first hotel
          console.log('❌ Saved hotel not found in list, using first hotel');
          dispatch({
            type: 'UPDATE_SEARCH_STATE',
            payload: { 
              hotel: hotelData[0].name, 
              hotelId: hotelData[0].id 
            }
          });
        }
      } else {
        // No saved hotel, use first from list
        console.log('📝 No saved hotelId, using first from list');
        dispatch({
          type: 'UPDATE_SEARCH_STATE',
          payload: { 
            hotel: hotelData[0].name, 
            hotelId: hotelData[0].id 
          }
        });
      }
      setInitialLoad(false);
    }
    } catch (err) {
      console.error('Failed to fetch hotels:', err);
    }
  }, [dispatch, initialLoad]);

  // Fetch hotels on mount
  useEffect(() => { 
    fetchHotels(); 
  }, [fetchHotels]);

 useEffect(() => {
  // Check if we should auto-search (from widget)
  const shouldAutoSearch = sessionStorage.getItem('shouldAutoSearch') === 'true' &&
                         state.searchState.hotelId && 
                         state.searchState.checkin && 
                         state.searchState.checkout &&
                         !state.searchState.searchPerformed &&
                         state.searchState.hotel !== "ABC Hotel"; // Not the default
  
  if (shouldAutoSearch && hotels.length > 0) {
    console.log('🚀 Auto-searching from widget data...');
    console.log('Hotel from widget:', state.searchState.hotel);
    console.log('Hotel ID:', state.searchState.hotelId);
    
    const performAutoSearch = async () => {
      try {
        setSearchLoading(true);
        
        // Format dates for API
        const formatDate = (d) => {
          if (!d) return '';
          const date = d instanceof Date ? d : new Date(d);
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        };
        
        const searchParams = {
          startDate: formatDate(state.searchState.checkin),
          endDate: formatDate(state.searchState.checkout),
          rooms: state.rooms.length,
          guests: state.searchState.guestCount,
          roomsData: state.rooms,
          promo_code:state.searchState.promo_code || null,
        };
        
        console.log('📊 Auto-search params:', searchParams);
        
        // IMPORTANT: Use hotelId from state (which came from widget)
        const hotelIdToSearch = state.searchState.hotelId;
        console.log('🔍 Searching for hotel ID:', hotelIdToSearch);
        
        // Call API with the widget's hotelId
        const hotelData = await apiService.getHotelData(hotelIdToSearch, searchParams);
        
        // Update state to mark search as performed
        dispatch({
          type: 'PERFORM_SEARCH',
          payload: {
            hotelId: hotelIdToSearch,
            checkin: state.searchState.checkin,
            checkout: state.searchState.checkout,
            rooms: state.rooms.length,
            guests: state.searchState.guestCount,
            apiResponse: hotelData,
            searchPerformed: true,
            promo_code:state.searchState.promo_code || null,
          }
        });
        
        console.log('✅ Auto-search completed successfully');
        
        // Clear the auto-search flag
        sessionStorage.removeItem('shouldAutoSearch');
        
      } catch (error) {
        console.error('❌ Auto-search failed:', error);
        sessionStorage.removeItem('shouldAutoSearch');
      } finally {
        setSearchLoading(false);
      }
    };
    
    // Trigger auto-search
    performAutoSearch();
  }
}, [state.searchState.hotelId, state.searchState.checkin, state.searchState.checkout, 
    state.searchState.searchPerformed, hotels, dispatch, state.searchState.hotel]);
  // Check for existing user session on component mount
  useEffect(() => {
  const userData = sessionStorage.getItem('userData'); // Changed from localStorage
  const userToken = sessionStorage.getItem('userToken'); // Changed from localStorage
  
  if (userData && userToken) {
    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      dispatch({ type: 'SET_USER', payload: parsedUser });
    } catch (error) {
      console.error('Error parsing user data:', error);
      sessionStorage.removeItem('userData'); // Changed from localStorage
      sessionStorage.removeItem('userToken'); // Changed from localStorage
    }
  }
}, [dispatch]);

  // Transform API categories to room format
  // const transformCategoriesToRooms = (categories, hotelInfo) => {
  //   return categories.map(category => ({
  //     id: category.category_id,
  //     name: category.category_name,
  //     size: category.size,
  //     guests: `Up to ${category.maxGuests} guests`,
  //     description: category.description,
  //      amenities: category.amenities || [], // Keep for backward compatibility
  //     features: category.features || {}, // Add this line for the new structure
  //     image: category.images && category.images.length > 0 ? category.images[0] : '/default-room.jpg',
  //     images: category.images || ['/default-room.jpg'],
  //     rates: category.rate_plans ? category.rate_plans.map(rate => ({
  //       id: rate.id.toString(),
  //       name: rate.name,
  //       price: rate.price,
  //       originalPrice: rate.originalPrice,
  //       offer: rate.offer || null,
  //       adult_pricing: rate.adult_pricing || [],
  //       child_pricing: rate.child_pricing || [],
  //       extra_bed_pricing: rate.extra_bed_pricing || [],
  //       extra_child_pricing: rate.extra_child_pricing || [],
  //        inclusions: rate.inclusions || []
  //     })) : [],
  //     other_offer: category.other_offer || [],
  //     available_rooms: category.available_rooms || [],
  //     currency: hotelInfo.currency_symbol || '₹',
  //     total_rooms: category.total_rooms || 0,
  //     maxGuests: category.maxGuests || 3,
  //     terms_and_conditions: category.terms_and_conditions || []
  //   }));
  // };


//   const transformCategoriesToRooms = (categories, hotelInfo) => {
//   return categories.map(category => ({
//     id: category.category_id,
//     name: category.category_name,
//     size: category.size,
//     guests: `Up to ${category.maxGuests} guests`,
//     description: category.description,
//     amenities: category.amenities || [], // backward compatibility
//     features: category.features || {},
//     image:
//       category.images && category.images.length > 0
//         ? category.images[0]
//         : '/default-room.jpg',
//     images: category.images || ['/default-room.jpg'],

//     rates:
//       category.total_rooms === 0
//         ? []
//         : category.rate_plans
//         ? category.rate_plans.map(rate => ({
//             id: rate.id.toString(),
//             name: rate.name,
//             price: rate.price,
//             originalPrice: rate.originalPrice,
//             offer: rate.offer || null,
//             adult_pricing: rate.adult_pricing || [],
//             child_pricing: rate.child_pricing || [],
//             extra_bed_pricing: rate.extra_bed_pricing || [],
//             extra_child_pricing: rate.extra_child_pricing || [],
//             inclusions: rate.inclusions || []
//           }))
//         : [],

//     other_offer: category.other_offer || [],
//     available_rooms: category.available_rooms || [],
//     currency: hotelInfo.currency_symbol || '₹',
//     total_rooms: category.total_rooms || 0,
//     maxGuests: category.maxGuests || 3,
//     terms_and_conditions: category.terms_and_conditions || []
//   }));
// };


const transformCategoriesToRooms = (categories, hotelInfo) => {
  return categories
    .filter(category => category.total_rooms !== 0) // 👈 remove empty categories
    .map(category => ({
      id: category.category_id,
      name: category.category_name,
      size: category.size,
      guests: `Up to ${category.maxGuests} guests`,
      description: category.description,
      amenities: category.amenities || [],
      features: category.features || {},
      image:
        category.images && category.images.length > 0
          ? category.images[0]
          : '/default-room.jpg',
      images: category.images || ['/default-room.jpg'],

      rates: category.rate_plans
        ? category.rate_plans.map(rate => ({
            id: rate.id.toString(),
            name: rate.name,
            price: rate.price,
            originalPrice: rate.originalPrice,
            offer: rate.offer || null,
            adult_pricing: rate.adult_pricing || [],
            child_pricing: rate.child_pricing || [],
            extra_bed_pricing: rate.extra_bed_pricing || [],
            extra_child_pricing: rate.extra_child_pricing || [],
            inclusions: rate.inclusions || []
          }))
        : [],

      other_offer: category.other_offer || [],
      available_rooms: category.available_rooms || [],
      currency: hotelInfo.currency_symbol || '₹',
      total_rooms: category.total_rooms,
      maxGuests: category.maxGuests || 3,
      terms_and_conditions: category.terms_and_conditions || []
    }));
};



  // Fetch hotel data function
  // const fetchHotelData = useCallback(async () => {
  //   if (!state.searchState.hotelId || !state.searchState.checkin || !state.searchState.checkout) {
  //     return;
  //   }

  //   const currentSearchParams = {
  //     hotelId: state.searchState.hotelId,
  //     checkin: state.searchState.checkin,
  //     checkout: state.searchState.checkout,
  //     rooms: state.rooms.length,
  //     guests: state.searchState.guestCount,
  //     roomsData: state.rooms
  //   };

  //   const paramsChanged = JSON.stringify(currentSearchParams) !== JSON.stringify(lastSearchParams);

  //   if (!paramsChanged && rooms.length > 0) {
  //     return;
  //   }

  //   try {
  //     setLoading(true);
  //     setError(null);

  //     const searchParams = {
  //       startDate: state.searchState.checkin,
  //       endDate: state.searchState.checkout,
  //       rooms: state.rooms.length,
  //       guests: state.searchState.guestCount,
  //       roomsData: state.rooms
  //     };

  //     const hotelData = await apiService.getHotelData(state.searchState.hotelId, searchParams);
      
  //     if (hotelData.categories) {
  //       const transformedRooms = transformCategoriesToRooms(hotelData.categories, hotelData.hotel_info);
  //       setRooms(transformedRooms);
  //     }
      
  //     setLastSearchParams(currentSearchParams);

  //   } catch (err) {
  //     // setError('Failed to load room data');
  //     console.error('Error fetching hotel data:', err);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [state.searchState.hotelId, state.searchState.checkin, state.searchState.checkout, state.rooms, state.searchState.guestCount, lastSearchParams, rooms.length]);

  // useEffect(() => {
  //   if (state.searchState.hotelId && state.searchState.checkin && state.searchState.checkout) {
  //     fetchHotelData();
  //   }
  // }, [fetchHotelData, state.searchState.hotelId, state.searchState.checkin, state.searchState.checkout]);

  // useEffect(() => {
  //   const debounceTimer = setTimeout(() => {
  //     if (state.searchState.hotelId && state.searchState.checkin && state.searchState.checkout) {
  //       fetchHotelData();
  //     }
  //   }, 500);

  //   return () => clearTimeout(debounceTimer);
  // }, [state.searchState.hotelId, state.searchState.checkin, state.searchState.checkout, state.rooms.length, state.searchState.guestCount, fetchHotelData]);

  // useEffect(() => {
  //   if (state.searchState.searchPerformed) {
  //     fetchHotelData();
  //   }
  // }, [state.searchState.searchPerformed, fetchHotelData]);


  const fetchHotelData = useCallback(async () => {
  if (!state.searchState.hotelId || !state.searchState.checkin || !state.searchState.checkout) {
    return;
  }

  const currentSearchParams = {
    hotelId: state.searchState.hotelId,
    checkin: state.searchState.checkin,
    checkout: state.searchState.checkout,
    rooms: state.rooms.length,
    guests: state.searchState.guestCount,
    roomsData: state.rooms
  };

  const paramsChanged = JSON.stringify(currentSearchParams) !== JSON.stringify(lastSearchParams);

  if (!paramsChanged && rooms.length > 0) {
    return;
  }

  try {
    setLoading(true);
    setError(null);

    const searchParams = {
      startDate: state.searchState.checkin,
      endDate: state.searchState.checkout,
      rooms: state.rooms.length,
      guests: state.searchState.guestCount,
      roomsData: state.rooms,
      promo_code:state.searchState.promo_code || null,
    };

    const hotelData = await apiService.getHotelData(state.searchState.hotelId, searchParams);
    
    // Handle empty categories - set empty rooms and update last params
    if (!hotelData.categories || hotelData.categories.length === 0) {
      console.log('No categories returned from API');
      setRooms([]);
      setLastSearchParams(currentSearchParams);
      setLoading(false);
      return; // Exit early
    }
    
    const transformedRooms = transformCategoriesToRooms(hotelData.categories, hotelData.hotel_info);
    setRooms(transformedRooms);
    setLastSearchParams(currentSearchParams);

  } catch (err) {
    console.error('Error fetching hotel data:', err);
  } finally {
    setLoading(false);
  }
}, [state.searchState.hotelId, state.searchState.checkin, state.searchState.checkout, state.rooms, state.searchState.guestCount, lastSearchParams]);
// REMOVED rooms.length from dependencies


// Replace all the useEffect hooks related to fetchHotelData with this single one
useEffect(() => {
  let isMounted = true;
  let timeoutId;

  const fetchData = async () => {
    if (!state.searchState.hotelId || !state.searchState.checkin || !state.searchState.checkout) {
      return;
    }

    const currentSearchParams = {
      hotelId: state.searchState.hotelId,
      checkin: state.searchState.checkin,
      checkout: state.searchState.checkout,
      rooms: state.rooms.length,
      guests: state.searchState.guestCount,
      roomsData: state.rooms
    };

    // Skip if params haven't changed
    if (JSON.stringify(currentSearchParams) === JSON.stringify(lastSearchParams)) {
      return;
    }

    // Skip if we're already loading
    if (loading) {
      return;
    }

    try {
      if (isMounted) {
        setLoading(true);
        setError(null);
      }

      const searchParams = {
        startDate: state.searchState.checkin,
        endDate: state.searchState.checkout,
        rooms: state.rooms.length,
        guests: state.searchState.guestCount,
        roomsData: state.rooms,
         promo_code:state.searchState.promo_code || null,
      };

      const hotelData = await apiService.getHotelData(state.searchState.hotelId, searchParams);
      
      if (!isMounted) return;

      // Handle empty categories
      if (!hotelData.categories || hotelData.categories.length === 0) {
        console.log('API returned empty categories');
        setRooms([]);
        setLastSearchParams(currentSearchParams);
        return;
      }
      
      const transformedRooms = transformCategoriesToRooms(hotelData.categories, hotelData.hotel_info);
      setRooms(transformedRooms);
      setLastSearchParams(currentSearchParams);

    } catch (err) {
      if (!isMounted) return;
      console.error('Error fetching hotel data:', err);
      // Don't set error to prevent showing error message for empty results
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  // Debounce the API call
  timeoutId = setTimeout(() => {
    fetchData();
  }, 500);

  // Cleanup function
  return () => {
    isMounted = false;
    clearTimeout(timeoutId);
  };
}, [
  state.searchState.hotelId,
  state.searchState.checkin,
  state.searchState.checkout,
  state.rooms.length,
  state.searchState.guestCount,
  state.rooms,
  lastSearchParams
  // Note: We're NOT including fetchHotelData in dependencies since we're not using it anymore
]);


  const handleImageModalOpen = (room) => {
    setSelectedRoom(room);
    setImageModalOpen(true);
  };

  const handleContinueToCheckout = () => {
    if (state.cart.length > 0) {
      navigateTo('booking');
    }
  };

  const handleLoginSuccess = (loginData) => {
  console.log('Login success data:', loginData);
  
    if (loginData && loginData.user) {
      setUser(loginData.user);
      dispatch({ 
        type: 'SET_USER', 
        payload: loginData.user 
      });
      setAuthModalOpen(false);
      
      // Show success toast/message
      alert(`Welcome ${loginData.user.name}! Login successful.`);
    } else {
      console.error('User data missing in login response');
    }
  };

  const handleLogout = () => {
    setUser(null);
    dispatch({ type: 'CLEAR_USER' });
    sessionStorage.removeItem('userToken');
    sessionStorage.removeItem('userData');
    console.log('User logged out');
  };

  const getUserDisplayName = () => {
    return user?.name || user?.email?.split('@')[0] || 'User';
  };

  return (
    <>
      <SearchBar />
      
      <div className="container mx-auto py-8 px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:gap-8">
          
          <div className="lg:w-2/3 w-full ">
            {!user && (
              /* VANSH START - Enhanced mobile banner with animation */
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 text-blue-800 px-4 py-4 rounded-lg relative mb-6 text-sm flex items-center gap-3 shadow-sm animate-[fadeIn_0.5s_ease-in] hidden">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-7 h-7 flex-shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
                <div>
                  <strong className="font-semibold block mb-1">Unlock Exclusive Rates!</strong>
                  <span className="block text-xs">
                    <button 
                      onClick={() => setAuthModalOpen(true)}
                      className="font-medium underline hover:text-blue-900 active:text-blue-950 transition-colors"
                    >
                      Login or Sign up
                    </button>
                    {' '}for access to special deals.
                  </span>
                </div>
              </div>
              /* VANSH END */
            )}

            {user && (
              /* VANSH START - Enhanced mobile welcome banner */
              <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 text-green-800 px-4 py-4 rounded-lg relative mb-6 text-sm flex items-center justify-between shadow-sm animate-[fadeIn_0.5s_ease-in]">
                <div className="flex items-center gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-7 h-7 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <strong className="font-semibold block mb-1">Welcome, {getUserDisplayName()}!</strong>
                    <span className="text-xs block">You're getting exclusive member rates.</span>
                  </div>
                </div>
                <button 
                  onClick={handleLogout}
                  className="text-green-700 hover:text-green-900 active:text-green-950 font-medium text-xs underline transition-colors whitespace-nowrap ml-2"
                >
                  Logout
                </button>
              </div>
              /* VANSH END */
            )}

            {loading && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent mx-auto"></div>
                <p className="mt-4 text-text-secondary">Loading available rooms...</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md mb-6">
                <p>{error}</p>
              </div>
            )}

            {!loading && !error && rooms.length === 0 && state.searchState.hotelId && (
              <div className="text-center py-8">
                <p className="text-text-secondary">No rooms available for the selected dates.</p>
              </div>
            )}

            {/* VANSH START - Added stagger animation for room cards on mobile */}
            <div className="space-y-6 pb-32 lg:pb-0">
              {rooms.map((room, index) => (
                <div
                  key={room.id}
                  className="animate-[fadeInUp_0.5s_ease-out]"
                  style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'both' }}
                >
                  <RoomCard
                    room={room}
                    onImageClick={handleImageModalOpen}
                    user={user}
                  />
                </div>
              ))}
            </div>
            {/* VANSH END */}
          </div>

          <div className="hidden lg:block lg:w-1/3 w-full">
            <BookingSidebar 
              onContinue={handleContinueToCheckout}
              user={user}
            />
          </div>
        </div>
      </div>

     {/* VANSH START - Mobile Floating Summary Bar - Only show when rooms are selected */}
      {state.cart.length > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.1)] z-40 safe-area-pb animate-[slideUp_0.3s_ease-out]">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className="flex items-center gap-2 text-sm font-medium text-text-primary active:scale-95 transition-transform"
              >
                <div className="text-left">
                  <div className="text-xs text-text-secondary">Total</div>
                  <div className="text-lg font-bold">{formatCurrency(state.finalTotal || state.total)}</div>
                </div>
                <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              <button
                onClick={handleContinueToCheckout}
                disabled={state.cart.reduce((acc, item) => acc + item.quantity, 0) !== state.rooms.length || state.cart.length === 0}
                className={`flex-1 bg-brand-accent text-text-dark-on-accent px-6 py-3 rounded-lg font-semibold text-base hover:bg-brand-accent-hover transition-all active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent shadow-lg ${
                  (state.cart.reduce((acc, item) => acc + item.quantity, 0) !== state.rooms.length || state.cart.length === 0) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      {/* VANSH END */}

      {isMobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 animate-[fadeIn_0.2s_ease-out]">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
          
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto shadow-2xl animate-[slideUp_0.3s_ease-out]">
            <div className="sticky top-0 bg-white pt-3 pb-2 px-4 border-b border-gray-200 rounded-t-2xl">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-3" />
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-text-primary">Booking Summary</h3>
                <button
                  onClick={() => setIsMobileSidebarOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors active:scale-95"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <BookingSidebar 
                onContinue={() => {
                  setIsMobileSidebarOpen(false);
                  handleContinueToCheckout();
                }}
                user={user}
              />
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        
        .safe-area-pb {
          padding-bottom: env(safe-area-inset-bottom, 0);
        }
      `}</style>
      {/* VANSH END */}

      {imageModalOpen && selectedRoom && (
        <ImageModal
          room={selectedRoom}
          onClose={() => setImageModalOpen(false)}
        />
      )}

      {authModalOpen && (
        <AuthModal 
          onClose={() => setAuthModalOpen(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
    </>
  );
};

export default RoomSelection;